/**
 * CalendarMonthView — CSS Grid month layout with event blocks.
 * Features: day numbers, today highlighting, weekend dimming, week numbers,
 * event blocks, multi-day spanning bars, +N more overflow.
 */
import React, { useMemo, useState } from 'react';
import { format, isSameDay, parseISO, isValid } from 'date-fns';
import { getMonthGrid, getEventDateRange, isAllDayEvent, isMultiDayEvent, type MonthGridDay } from './calendarDateUtils';
import { positionEventsInDay, resolveEventColor, type CalendarEvent } from './calendarEventPositioning';
import { CalendarEventBlock } from './CalendarEventBlock';
import { pmTheme } from './pmTheme';

interface CalendarMonthViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  firstDayOfWeek: number;
  showWeekends: boolean;
  showWeekNumbers: boolean;
  maxEventsPerDay: number;
  showEventTime: boolean;
  colorMap?: Record<string, string>;
  isViewerMode: boolean;
  onEventClick?: (event: CalendarEvent) => void;
  onDayClick?: (date: Date) => void;
  onEventHover?: (e: React.MouseEvent, event: CalendarEvent) => void;
  onEventHoverEnd?: () => void;
}

const DAY_NAMES_MON = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_NAMES_SUN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const CalendarMonthView: React.FC<CalendarMonthViewProps> = ({
  currentDate,
  events,
  firstDayOfWeek,
  showWeekends,
  showWeekNumbers,
  maxEventsPerDay,
  showEventTime,
  colorMap,
  isViewerMode,
  onEventClick,
  onDayClick,
  onEventHover,
  onEventHoverEnd,
}) => {
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  const grid = useMemo(
    () => getMonthGrid(currentDate, firstDayOfWeek),
    [currentDate, firstDayOfWeek],
  );

  const dayNames = firstDayOfWeek === 0 ? DAY_NAMES_SUN : DAY_NAMES_MON;
  const visibleDayNames = showWeekends ? dayNames : dayNames.filter(d => d !== 'Sat' && d !== 'Sun');
  const colCount = showWeekNumbers ? visibleDayNames.length + 1 : visibleDayNames.length;

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const dates = getEventDateRange(event.start, event.end);
      for (const d of dates) {
        const key = format(d, 'yyyy-MM-dd');
        const arr = map.get(key) || [];
        arr.push(event);
        map.set(key, arr);
      }
    }
    return map;
  }, [events]);

  return (
    <div style={{ width: '100%', userSelect: 'none' }}>
      {/* Day name headers */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: showWeekNumbers
            ? `40px repeat(${visibleDayNames.length}, 1fr)`
            : `repeat(${visibleDayNames.length}, 1fr)`,
          borderBottom: `1px solid ${pmTheme.border}`,
        }}
      >
        {showWeekNumbers && (
          <div style={{ padding: '6px 4px', fontSize: '10px', color: pmTheme.textSubtle, textAlign: 'center' }}>Wk</div>
        )}
        {visibleDayNames.map(name => (
          <div
            key={name}
            data-testid="calendar-day-header"
            style={{
              padding: '6px 4px',
              fontSize: '11px',
              fontWeight: 600,
              color: pmTheme.textMuted,
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {name}
          </div>
        ))}
      </div>

      {/* Grid rows */}
      {grid.map((week, weekIdx) => {
        const filteredWeek = showWeekends ? week : week.filter(d => !d.isWeekend);
        return (
          <div
            key={weekIdx}
            style={{
              display: 'grid',
              gridTemplateColumns: showWeekNumbers
                ? `40px repeat(${filteredWeek.length}, 1fr)`
                : `repeat(${filteredWeek.length}, 1fr)`,
              minHeight: 90,
              borderBottom: `1px solid ${pmTheme.borderSubtle}`,
            }}
          >
            {showWeekNumbers && (
              <div
                data-testid={`calendar-week-number-${week[0].weekNumber}`}
                style={{
                  padding: '4px',
                  fontSize: '10px',
                  color: pmTheme.textSubtle,
                  textAlign: 'center',
                  borderRight: `1px solid ${pmTheme.borderSubtle}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                }}
              >
                {week[0].weekNumber}
              </div>
            )}
            {filteredWeek.map(day => {
              const dateKey = format(day.date, 'yyyy-MM-dd');
              const dayEvents = eventsByDate.get(dateKey) || [];
              const layout = positionEventsInDay(dayEvents, maxEventsPerDay);
              const isExpanded = expandedDay === dateKey;

              return (
                <div
                  key={dateKey}
                  data-testid={day.isToday ? 'calendar-today' : `calendar-day-${day.date.getDate()}`}
                  aria-label={format(day.date, 'EEEE, MMMM d, yyyy')}
                  role="gridcell"
                  aria-selected={day.isToday}
                  style={{
                    padding: '2px 4px',
                    borderRight: `1px solid ${pmTheme.borderSubtle}`,
                    backgroundColor: day.isToday
                      ? pmTheme.primarySoft
                      : day.isWeekend
                        ? pmTheme.surfaceSubtle
                        : 'transparent',
                    opacity: day.isCurrentMonth ? 1 : 0.4,
                    cursor: isViewerMode ? 'pointer' : 'default',
                    minHeight: 80,
                    overflow: 'hidden',
                  }}
                  onClick={() => {
                    if (isViewerMode) onDayClick?.(day.date);
                  }}
                >
                  {/* Day number */}
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: day.isToday ? 700 : 400,
                      color: day.isToday ? pmTheme.primary : day.isCurrentMonth ? pmTheme.text : pmTheme.textSubtle,
                      textAlign: 'right',
                      marginBottom: 2,
                      ...(day.isToday && {
                        backgroundColor: pmTheme.primary,
                        color: pmTheme.textInverse,
                        borderRadius: '50%',
                        width: 22,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: 'auto',
                      }),
                    }}
                  >
                    {day.date.getDate()}
                  </div>

                  {/* Events */}
                  {isExpanded
                    ? dayEvents.map((evt, idx) => (
                        <CalendarEventBlock
                          key={evt.id + '-' + idx}
                          event={evt}
                          colorMap={colorMap}
                          showTime={showEventTime}
                          isMultiDay={isMultiDayEvent({ start: evt.start, end: evt.end })}
                          onClick={onEventClick}
                          onMouseEnter={onEventHover}
                          onMouseLeave={onEventHoverEnd}
                        />
                      ))
                    : layout.visibleEvents.map((item, idx) => (
                        <CalendarEventBlock
                          key={item.event.id + '-' + idx}
                          event={item.event}
                          colorMap={colorMap}
                          showTime={showEventTime}
                          isMultiDay={item.isMultiDay}
                          onClick={onEventClick}
                          onMouseEnter={onEventHover}
                          onMouseLeave={onEventHoverEnd}
                        />
                      ))}

                  {/* Overflow indicator */}
                  {!isExpanded && layout.overflowCount > 0 && (
                    <div
                      data-testid={`calendar-overflow-${dateKey}`}
                      style={{
                        fontSize: '11px',
                        color: pmTheme.textMuted,
                        cursor: 'pointer',
                        padding: '1px 4px',
                        fontWeight: 500,
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        setExpandedDay(dateKey);
                      }}
                    >
                      +{layout.overflowCount} more
                    </div>
                  )}
                  {isExpanded && dayEvents.length > maxEventsPerDay && (
                    <div
                      style={{ fontSize: '10px', color: pmTheme.textMuted, cursor: 'pointer', padding: '1px 4px' }}
                      onClick={e => {
                        e.stopPropagation();
                        setExpandedDay(null);
                      }}
                    >
                      Show less
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
